import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobal } from '../context/GlobalContext';

// Import icons directly so they get bundled with the app
import costTrackerIcon from '../assets/icons/CostTracker.png';
import milestonesIcon from '../assets/icons/Milestones.png';
import checklistsIcon from '../assets/icons/Checklists.png';
import governmentIcon from '../assets/icons/GovernmentResources.png';
import findBusinessIcon from '../assets/icons/FindABusiness.png';
import documentsIcon from '../assets/icons/Documents.png';
import builderLicenceIcon from '../assets/icons/BuilderLicenseCheck.png';
import insuranceIcon from '../assets/icons/BuildingInsuranceCheck.png';
import solarIcon from '../assets/icons/SolarOrientationGuide.png';
import defectsIcon from '../assets/icons/DefectsScanner.png';
import findBuilderIcon from '../assets/icons/FindABuilder.png';
import snapStyleIcon from '../assets/icons/SnapAndStyle.png';
import quoteComparisonIcon from '../assets/icons/QuoteComparison.png';

const tools = [
  { id: 'cost-tracker', name: 'Cost Tracker', icon: costTrackerIcon, path: '/tools/cost-tracker' },
  { id: 'milestones', name: 'Milestones', icon: milestonesIcon, path: '/tools/milestones' },
  { id: 'checklists', name: 'Checklists', icon: checklistsIcon, path: '/tools/checklists' },
  { id: 'government-resources', name: 'Government Resources', icon: governmentIcon, path: '/tools/government-resources' },
  { id: 'find-business', name: 'Find a Business', icon: findBusinessIcon, path: '/tools/find-a-business' },
  { id: 'documents', name: 'Documents', icon: documentsIcon, path: '/tools/documents' },
  { id: 'builder-licence', name: 'Builder Licence Check', icon: builderLicenceIcon, path: '/tools/builder-licence-check' },
  { id: 'insurance', name: 'Building Insurance Check', icon: insuranceIcon, path: '/tools/building-insurance-check' },
  { id: 'solar', name: 'Solar Orientation Guide', icon: solarIcon, path: '/tools/solar-orientation' },
  { id: 'defects', name: 'Defects Scanner', icon: defectsIcon, path: 'https://buildview-ai.emergent.host?v=2', external: true, premium: true },
  { id: 'find-builder', name: 'Find a Builder', icon: findBuilderIcon, path: 'https://builder-match-4.emergent.host?v=2', external: true, premium: true },
  { id: 'snap-style', name: 'Snap and Style', icon: snapStyleIcon, path: 'https://villanoir-scan.emergent.host', external: true, premium: true },
  { id: 'quote-comparison', name: 'Quote & Inclusions Comparison', icon: quoteComparisonIcon, path: '/tools/quote-comparison', premium: true },
  { id: 'facade-generator', name: 'Facade Visualiser', icon: 'https://customer-assets.emergentagent.com/job_b0cd57bc-cd2c-4d8e-855c-936980c634c7/artifacts/9iy1lvj1_6AA8E368-E547-46DC-BA51-EB15A456A3EE.png', path: '/tools/facade-generator', premium: true },
  { id: 'buildpilot-intelligence', name: 'Project Health', icon: 'https://customer-assets.emergentagent.com/job_smart-builder-14/artifacts/231u5dm5_C4A99C8E-2954-4650-8EFA-0F4C9217A9C7.png', path: '/tools/buildpilot-intelligence', premium: true }
];

const Toolbox = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const projectId = searchParams.get('projectId');
  const [showAd, setShowAd] = useState(false);
  
  // Get subscription status from GlobalContext (single source of truth)
  const { hasActiveSubscription } = useGlobal() || {};
  
  console.log('Toolbox: hasActiveSubscription =', hasActiveSubscription, 'showAd =', showAd);

  // Filter tools based on subscription
  // null = still loading (show all tools), true = premium (show all), false = not premium (hide premium)
  const visibleTools = tools.filter(tool => {
    // Only hide premium tools if subscription is definitively FALSE (not loading)
    if (tool.premium && hasActiveSubscription === false) {
      return false;
    }
    return true;
  });

  // Stage 1: Show ad container for free users (when subscription check is complete and false)
  useEffect(() => {
    if (hasActiveSubscription === false) {
      console.log('Toolbox: Setting showAd to true');
      setShowAd(true);
    }
  }, [hasActiveSubscription]);

  // Stage 2: Initialize AdSense AFTER the ad container is rendered
  useEffect(() => {
    if (showAd) {
      console.log('Toolbox: showAd is true, pushing to AdSense');
      // Wait for next render cycle to ensure DOM element exists
      const timer = setTimeout(() => {
        try {
          const adElement = document.querySelector('[data-testid="toolbox-ad-banner"] .adsbygoogle');
          console.log('Toolbox: Ad element found:', !!adElement);
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          console.log('Toolbox: AdSense pushed successfully');
        } catch (e) {
          console.error('Toolbox AdSense error:', e);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showAd]);

  return (
    <div className="min-h-screen pb-8" data-testid="toolbox-page" style={{ background: '#FFBD59' }}>
      {/* Tools Grid - centered with padding */}
      <div className="px-6 py-8">
        <div className="grid grid-cols-3 gap-6" data-testid="tools-grid">
          {visibleTools.map((tool) => (
            <div
              key={tool.id}
              className="tool-card flex flex-col items-center cursor-pointer"
              onClick={() => {
                if (tool.external) {
                  window.location.href = tool.path;
                } else {
                  // Pass userId and projectId to internal tools
                  const params = new URLSearchParams();
                  if (userId) params.append('userId', userId);
                  if (projectId) params.append('projectId', projectId);
                  const pathWithParams = params.toString() ? `${tool.path}?${params.toString()}` : tool.path;
                  navigate(pathWithParams);
                }
              }}
              data-testid={`tool-card-${tool.id}`}
            >
              <div 
                className="relative mb-2" 
                style={{ 
                  width: '70px', 
                  height: '70px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img 
                  src={tool.icon} 
                  alt={tool.name}
                  className="w-full h-full"
                  style={{ 
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                />
                {/* "Scan Now" badge for BuildPilot Intelligence */}
                {tool.id === 'buildpilot-intelligence' && (
                  <div className="absolute -top-2 -right-2 animated-gradient-bg px-1.5 py-0.5 rounded-full shadow-md flex items-center justify-center">
                    <span className="text-[7px] md:text-[8px] font-bold text-white whitespace-nowrap leading-none">Scan Now</span>
                  </div>
                )}
              </div>
              <p 
                className="text-center font-medium leading-tight"
                style={{ 
                  color: '#000000',
                  fontSize: '12px',
                  minHeight: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {tool.name}
              </p>
            </div>
          ))}
        </div>

        {/* Fixed Ad Banner for Free Users - shown below tools */}
        {showAd && !hasActiveSubscription && (
          <div className="mt-6 mx-auto w-full px-4" data-testid="toolbox-ad-banner">
            <ins
              className="adsbygoogle"
              style={{ display: 'block' }}
              data-ad-client="ca-pub-9269078139959101"
              data-ad-slot="6037841373"
              data-ad-format="auto"
              data-full-width-responsive="true"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Toolbox;